import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { makeStyles } from '@mui/styles';

// One accent per column, in the order the design lays them out.
const AccentColors = ['#002547', '#50B2B2', '#BA1A1A'];

const POINTER_COUNT = AccentColors.length;

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(7),
    width: '100%'
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3)
  },
  pointers: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(7)
  },
  pointer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    // Vertical padding gives the accent border room beyond the text block.
    padding: theme.spacing(1, 0, 1, 6)
  },
  category: {
    fontSize: '12px',
    lineHeight: '14px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  bulletRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  },
  bullet: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
    // Sits on the middle of the first line rather than the top of the block:
    // half the 24px line height, less half the dot.
    marginTop: '9px'
  },
  body: {
    lineHeight: '24px'
  }
}));

// The API returns a plain sentence per pointer today. An object shape is
// rendered in full, so a richer response needs no change here.
const toPointer = (pointer) => (
  typeof pointer === 'string'
    ? { description: pointer }
    : {
      category: pointer?.category,
      title: pointer?.title,
      description: pointer?.description || pointer?.pointer || ''
    }
);

const ImprovementPointers = ({ pointers = [], loading = false }) => {
  const classes = useStyles();

  const items = React.useMemo(() => pointers.map(toPointer), [pointers]);

  if (!loading && !items.length) return null;

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <TrendingUpIcon sx={{ fontSize: '26px', color: AccentColors[0] }} />
        <Typography variant='h5-semiBold' sx={{ color: AccentColors[0] }}>
          What to improve next time?
        </Typography>
      </Box>

      <Box className={classes.pointers}>
        {loading
          ? [...Array(POINTER_COUNT)].map((value, index) => (
            <Box
              key={index}
              className={classes.pointer}
              sx={{ borderLeft: `3px solid ${AccentColors[index]}` }}
            >
              <Skeleton variant='text' width='70%' height={16} />
              <Skeleton variant='rectangular' width='100%' height={92} />
            </Box>
          ))
          : items.map(({ category, title, description }, index) => (
            <Box
              key={index}
              className={classes.pointer}
              sx={{ borderLeft: `3px solid ${AccentColors[index % POINTER_COUNT]}` }}
            >
              {category && (
                <Typography
                  variant='body02-semiBold'
                  className={classes.category}
                  sx={{ color: AccentColors[index % POINTER_COUNT] }}
                >
                  {category}
                </Typography>
              )}

              {title && (
                <Typography variant='body01-semiBold' color='neutral.clr-900'>
                  {title}
                </Typography>
              )}

              <Box className={classes.bulletRow}>
                <Box
                  className={classes.bullet}
                  sx={{ backgroundColor: AccentColors[index % POINTER_COUNT] }}
                />

                <Typography
                  variant='body01-regular'
                  color='neutral.clr-600'
                  className={classes.body}
                >
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default ImprovementPointers;
